import React, { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Typography } from '@material-ui/core';
import { useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { getApiErrorMessage } from '../../../../shared/api/getApiErrorMessage';
import { DIAM_TEAM_ID } from '../../diamApi';
import { Stepper } from '../../../Stepper/Stepper';
import { DiamDrawer } from '../DiamDrawer/DiamDrawer';
import { parseAssignIdentityRoleIds } from './parseAssignIdentityContents';
import {
  ASSIGN_IDENTITY_STEP_IDS,
  ASSIGN_IDENTITY_STEP_LABELS,
  ASSIGN_IDENTITY_STEPS,
  getAssignIdentityStepFields,
} from './stepsConfig';
import { FooterActions } from './styles';
import { AddRolesAndAttributesStep } from './steps/AddRolesAndAttributesStep';
import { SelectTeamsStep } from './steps/SelectTeamsStep';
import { AssignIdentityDrawerProps, AssignIdentityFormValues } from './types';
import { useAssignIdentityDrawerOptions } from './useAssignIdentityDrawerOptions';
import { useAssignMembershipRoles } from './useAssignMembershipRoles';
import {
  assignIdentityValidationSchema,
  DEFAULT_ASSIGN_IDENTITY_VALUES,
} from './validationSchema';

const FIRST_STEP_INDEX = 0;
const LAST_STEP_INDEX = ASSIGN_IDENTITY_STEPS.length - 1;

export function AssignIdentityDrawer({
  identity,
  isOpen,
  handleDrawerClose,
}: AssignIdentityDrawerProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [step, setStep] = useState(FIRST_STEP_INDEX);
  const optionsQuery = useAssignIdentityDrawerOptions(identity?.id, isOpen);
  const assignRoles = useAssignMembershipRoles(identity?.id);
  const { control, trigger, reset, handleSubmit, clearErrors } =
    useForm<AssignIdentityFormValues>({
      resolver: yupResolver(assignIdentityValidationSchema),
      defaultValues: DEFAULT_ASSIGN_IDENTITY_VALUES,
      mode: 'onChange',
    });

  const currentStep = ASSIGN_IDENTITY_STEPS[step];
  const isFirstStep = step === FIRST_STEP_INDEX;
  const isLastStep = step === LAST_STEP_INDEX;
  const isOptionsLoading = optionsQuery.isPending;
  const isSubmitting = assignRoles.isPending;
  const hasAssignableRoles = (optionsQuery.data?.assignableRoleCount ?? 0) > 0;

  useEffect(() => {
    if (!isOpen) return;

    reset(DEFAULT_ASSIGN_IDENTITY_VALUES);
    setStep(FIRST_STEP_INDEX);
  }, [isOpen, reset]);

  useEffect(() => {
    if (!isOpen || !optionsQuery.data) {
      return;
    }

    reset(current => ({
      ...current,
      teamIds: [`team:${DIAM_TEAM_ID}`],
    }));
  }, [isOpen, optionsQuery.data, reset]);

  useEffect(() => {
    if (!optionsQuery.isError) {
      return;
    }

    enqueueSnackbar(
      getApiErrorMessage(
        optionsQuery.error,
        'Failed to load assign role options',
      ),
      { variant: 'error' },
    );
  }, [enqueueSnackbar, optionsQuery.error, optionsQuery.isError]);

  function resetDrawer() {
    reset(DEFAULT_ASSIGN_IDENTITY_VALUES);
    setStep(FIRST_STEP_INDEX);
  }

  function onClose() {
    resetDrawer();
    handleDrawerClose();
  }

  async function goNext() {
    const fields = getAssignIdentityStepFields(step);
    const isValid = fields.length === 0 ? true : await trigger(fields);

    if (!isValid || isLastStep) return;
    setStep(currentStepIndex => currentStepIndex + 1);
  }

  function goPrevious() {
    if (isFirstStep) return;
    setStep(currentStepIndex => currentStepIndex - 1);
  }

  async function onSubmit(values: AssignIdentityFormValues) {
    const roleIds = parseAssignIdentityRoleIds(values.contents);
    if (roleIds.length === 0) {
      return;
    }

    try {
      await assignRoles.mutateAsync(roleIds.map(id => ({ id, modifiers: {} })));
      onClose();
      enqueueSnackbar('Role successfully assigned', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Failed to assign role'), {
        variant: 'error',
      });
    }
  }

  function handleAssign() {
    clearErrors();
    handleSubmit(onSubmit)();
  }

  function renderStepContent() {
    const teamTree = optionsQuery.data?.teamTree ?? [];
    const rolesTree = optionsQuery.data?.rolesTree ?? [];

    switch (currentStep.id) {
      case ASSIGN_IDENTITY_STEP_IDS.teams:
        return (
          <SelectTeamsStep
            control={control}
            items={teamTree}
            disabled={isOptionsLoading || isSubmitting}
          />
        );
      case ASSIGN_IDENTITY_STEP_IDS.rolesAndAttributes:
        return (
          <>
            {!isOptionsLoading && !hasAssignableRoles ? (
              <Typography color="textSecondary">
                This identity already has every team role assigned for{' '}
                {optionsQuery.data?.teamName ?? 'this team'}.
              </Typography>
            ) : null}
            <AddRolesAndAttributesStep
              control={control}
              items={rolesTree}
              disabled={isOptionsLoading || isSubmitting || !hasAssignableRoles}
            />
          </>
        );
      default:
        return null;
    }
  }

  let primaryActionLabel = 'Next';
  if (isLastStep) {
    primaryActionLabel = isSubmitting ? 'Assigning…' : 'Assign';
  }

  return (
    <DiamDrawer
      isOpen={isOpen}
      handleDrawerClose={onClose}
      title="Assign Role"
      sectionTitle={
        identity ? `${identity.name} · ${identity.email}` : undefined
      }
      confirmOnClose
      confirmTitle="Are you sure you want to cancel?"
      confirmContent="All the details provided will be lost."
      confirmButtonText="Cancel role assignment"
      cancelButtonText="Dismiss"
      headerCenter={
        <Stepper
          steps={ASSIGN_IDENTITY_STEP_LABELS}
          step={step}
          next={goNext}
          previous={goPrevious}
        />
      }
    >
      {renderStepContent()}
      <FooterActions>
        {!isFirstStep && (
          <Button
            variant="outlined"
            color="primary"
            onClick={goPrevious}
            disabled={isSubmitting}
          >
            Back
          </Button>
        )}
        <Button
          variant="contained"
          color="primary"
          onClick={isLastStep ? handleAssign : goNext}
          disabled={
            isOptionsLoading ||
            isSubmitting ||
            (isLastStep && !hasAssignableRoles)
          }
        >
          {primaryActionLabel}
        </Button>
      </FooterActions>
    </DiamDrawer>
  );
}
